import React, { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ProductImage } from '../types';
import { useTranslation } from '../i18n';
import { useIsTouchPrimary } from '../hooks/useMediaQuery';

export type PhotoDraft =
  | { kind: 'existing'; id: number; imageUrl: string }
  | { kind: 'pending'; key: string; file: File; previewUrl: string };

export function photoDraftsFromProduct(images?: ProductImage[]): PhotoDraft[] {
  return (images ?? []).map(img => ({
    kind: 'existing',
    id: img.id,
    imageUrl: img.imageUrl,
  }));
}

export function revokePendingPreviews(photos: PhotoDraft[]) {
  photos.filter((p): p is Extract<PhotoDraft, { kind: 'pending' }> => p.kind === 'pending')
    .forEach(p => URL.revokeObjectURL(p.previewUrl));
}

function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function previewUrl(photo: PhotoDraft): string {
  return photo.kind === 'existing' ? photo.imageUrl : photo.previewUrl;
}

function photoKey(photo: PhotoDraft, index: number): string {
  return photo.kind === 'existing' ? `existing-${photo.id}` : photo.key || `pending-${index}`;
}

function movePhoto(photos: PhotoDraft[], from: number, to: number): PhotoDraft[] {
  const reordered = [...photos];
  const [moved] = reordered.splice(from, 1);
  reordered.splice(to, 0, moved);
  return reordered;
}

interface PhotoManagerProps {
  photos: PhotoDraft[];
  onChange: (photos: PhotoDraft[]) => void;
  disabled?: boolean;
  maxPhotos?: number;
}

export const PhotoManager: React.FC<PhotoManagerProps> = ({
  photos,
  onChange,
  disabled = false,
  maxPhotos = 10,
}) => {
  const { t } = useTranslation();
  const isTouchPrimary = useIsTouchPrimary();
  const galleryRef = useRef<HTMLInputElement>(null);
  const atMax = photos.length >= maxPhotos;
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const addFiles = useCallback((acceptedFiles: File[]) => {
    const slotsLeft = maxPhotos - photos.length;
    if (slotsLeft <= 0 || acceptedFiles.length === 0) return;

    const existingKeys = new Set(
      photos
        .filter((p): p is Extract<PhotoDraft, { kind: 'pending' }> => p.kind === 'pending')
        .map(p => fileKey(p.file)),
    );

    const toAdd: PhotoDraft[] = [];
    for (const file of acceptedFiles) {
      if (toAdd.length >= slotsLeft) break;
      const key = fileKey(file);
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      toAdd.push({
        kind: 'pending',
        key: `${key}-${Date.now()}-${toAdd.length}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (toAdd.length > 0) {
      onChange([...photos, ...toAdd]);
    }
  }, [maxPhotos, onChange, photos]);

  const removePhoto = (index: number) => {
    const photo = photos[index];
    if (photo.kind === 'pending') {
      URL.revokeObjectURL(photo.previewUrl);
    }
    onChange(photos.filter((_, i) => i !== index));
  };

  const reorder = (from: number, to: number) => {
    if (disabled || from === to || to < 0 || to >= photos.length) return;
    onChange(movePhoto(photos, from, to));
  };

  const endDrag = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDragStart = (index: number) => (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox only starts a drag when some data is attached.
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index: number) => (e: React.DragEvent<HTMLDivElement>) => {
    if (dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(index);
  };

  const handleDrop = (index: number) => (e: React.DragEvent<HTMLDivElement>) => {
    if (dragIndex === null) return;
    e.preventDefault();
    e.stopPropagation();
    reorder(dragIndex, index);
    endDrag();
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) addFiles(files);
    e.target.value = '';
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    disabled: disabled || atMax,
    noClick: true,
    noKeyboard: false,
    onDrop: addFiles,
  });

  return (
    <div className="photo-manager">
      {photos.length === 0 && (
        <p className="photo-manager__empty text-muted">{t('photos.empty_hint')}</p>
      )}

      {photos.length > 1 && (
        <p className="photo-manager__reorder-hint text-muted">
          {isTouchPrimary ? t('photos.reorder_hint_touch') : t('photos.reorder_hint')}
        </p>
      )}

      {photos.length > 0 && (
        <div className="photo-manager__grid" role="list">
          {photos.map((photo, index) => (
            <div
              key={photoKey(photo, index)}
              className={[
                'photo-manager__thumb',
                dragIndex === index ? 'photo-manager__thumb--dragging' : '',
                dropIndex === index && dragIndex !== null && dragIndex !== index
                  ? 'photo-manager__thumb--drop-target'
                  : '',
              ].filter(Boolean).join(' ')}
              role="listitem"
              draggable={!disabled && photos.length > 1}
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={endDrag}
            >
              <img src={previewUrl(photo)} alt="" draggable={false} />
              <button
                type="button"
                className="photo-manager__remove"
                onClick={() => removePhoto(index)}
                disabled={disabled}
                aria-label={t('photos.remove')}
              >
                ×
              </button>
              {photos.length > 1 && (
                <div className="photo-manager__move">
                  <button
                    type="button"
                    onClick={() => reorder(index, index - 1)}
                    disabled={disabled || index === 0}
                    aria-label={t('photos.move_left')}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={() => reorder(index, index + 1)}
                    disabled={disabled || index === photos.length - 1}
                    aria-label={t('photos.move_right')}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="photo-manager__count text-muted">
        {t('photos.count', { current: photos.length, max: maxPhotos })}
      </p>

      <div
        {...getRootProps({
          className: [
            'photo-manager__add-zone',
            isDragActive ? 'photo-manager__add-zone--active' : '',
            disabled || atMax ? 'photo-manager__add-zone--disabled' : '',
          ].filter(Boolean).join(' '),
        })}
      >
        <input {...getInputProps()} />
        {(atMax || !isTouchPrimary) && (
          <p className="photo-manager__drop-hint">
            {atMax ? t('photos.max_reached') : t('photos.drop_hint')}
          </p>
        )}
        <div className="photo-manager__actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => galleryRef.current?.click()}
            disabled={disabled || atMax}
          >
            {t('photos.add')}
          </button>
        </div>
      </div>

      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="photo-manager__hidden-input"
        onChange={handleFileInput}
        disabled={disabled || atMax}
        tabIndex={-1}
        aria-hidden
      />
    </div>
  );
};
